import React, { useCallback, useEffect, useMemo, useState } from 'react';
import Chart from 'react-apexcharts';
import type { ApexOptions } from 'apexcharts';
import { RotateCcw } from 'react-feather';
import { toast } from 'react-toastify';
import {
  COHORTS,
  Cohort,
  REPORTS_ENABLED,
  SalesCountry,
  SalesDailyRow,
  SalesOwnerMonthly,
  SalesOwnerRow,
  SalesCompare,
  SalesSummary,
  fetchSalesDaily,
  fetchSalesOwnerMonthly,
  fetchSalesSummary,
} from '../../../Reports/reportsApi';
import { Page, PageHeader, Card, Button, Field, Select, TextInput, Toolbar, Table, Loader, EmptyState } from '../../../ui';
import SegmentedControl from './ui-pending/SegmentedControl';
import Tabs from './ui-pending/Tabs';
import StatTile from './ui-pending/StatTile';
import ProgressBar from './ui-pending/ProgressBar';
import ThisWeeksIncentive from './ThisWeeksIncentive';
import WhereIStand from './WhereIStand';
import './sales.scss';

/**
 * Sales → Sales Agent Performance.
 *
 * The CMS version of the Operations Hub (Streamlit) dashboard of the same
 * name. The numbers come from Snowflake views through artium-reports, so the
 * two agree: leads assigned = completed demos by IST demo day, converted = by
 * IST conversion day, revenue = the student's payment nearest the conversion.
 * The definitions live in the views (snowflake-apps/sql), not here.
 *
 * Two views of one page: a SALES_ADMIN sees every agent, a SALES_AGENT only
 * themselves. artium-reports decides which and filters the data; the page
 * renders whatever `summary.viewer` says it got. The role comes from the CMS
 * login; until the CMS has those roles, everyone gets the admin view.
 */

const COUNTRIES: { key: SalesCountry; label: string; flag: string; threshold: number }[] = [
  // Conversion % below the threshold shows red, as on the Streamlit dashboard.
  { key: 'India', label: 'India', flag: '🇮🇳', threshold: 18 },
  { key: 'USA', label: 'USA', flag: '🇺🇸', threshold: 35 },
  { key: 'ROW', label: 'Rest of world', flag: '🌍', threshold: 25 },
];

// Chart series colours (apexcharts config only): the design-system token values.
const BLUE = '#4101f7'; // --ds-primary
const GREEN = '#1f8a4c'; // --ds-success
const AMBER = '#b45309'; // --ds-warning
const ORANGE = '#dd30b1'; // --ds-accent
const PURPLE = '#2f5fd0'; // --ds-info

const num = (n: number): string => Math.round(n).toLocaleString('en-IN');
const inr = (n: number): string => `₹${num(n)}`;
const pct1 = (n: number): string => `${n.toFixed(1)}%`;
const ratio = (part: number, whole: number): number => (whole > 0 ? Math.round((part / whole) * 1000) / 10 : 0);

/** Today's date in IST, "YYYY-MM-DD" — the views count IST days. */
const istToday = (): string => new Date().toLocaleDateString('en-CA', { timeZone: 'Asia/Kolkata' });

const parseIso = (iso: string): Date => {
  const [y, m, d] = iso.split('-').map(Number);
  return new Date(Date.UTC(y, m - 1, d));
};
const toIso = (d: Date): string => d.toISOString().slice(0, 10);

const dayLabel = (iso: string): string =>
  parseIso(iso).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', timeZone: 'UTC' });
const longDay = (iso: string): string =>
  parseIso(iso).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric', timeZone: 'UTC' });
const monthLabel = (ym: string): string =>
  parseIso(`${ym}-01`).toLocaleDateString('en-GB', { month: 'short', year: 'numeric', timeZone: 'UTC' });

const eachDay = (from: string, to: string): string[] => {
  const days: string[] = [];
  for (let d = parseIso(from); toIso(d) <= to; d = new Date(d.getTime() + 864e5)) days.push(toIso(d));
  return days;
};

/** The last 12 months, newest first, as "YYYY-MM". */
const recentMonths = (today: string): string[] => {
  const [y, m] = today.split('-').map(Number);
  return Array.from({ length: 12 }, (_, i) => toIso(new Date(Date.UTC(y, m - 1 - i, 1))).slice(0, 7));
};

/**
 * This week: always Sunday to Saturday. Only the days so far have data, so
 * the range runs from Sunday to today.
 */
const weekRange = (today: string): { from: string; to: string } => {
  const sundayOffset = parseIso(today).getUTCDay(); // 0 = Sunday
  return { from: toIso(new Date(parseIso(today).getTime() - sundayOffset * 864e5)), to: today };
};

type RangeMode = 'today' | 'week' | 'month' | 'custom';
const RANGE_MODES: { key: RangeMode; label: string; title?: string }[] = [
  { key: 'today', label: 'Today' },
  { key: 'week', label: 'This week', title: 'Sunday to Saturday' },
  { key: 'month', label: 'Month' },
  { key: 'custom', label: 'Custom range' },
];

/** What each range is compared with: Today vs yesterday, This week vs last week, else a month back. */
const COMPARE_FOR: Record<RangeMode, SalesCompare> = { today: 'day', week: 'week', month: 'month', custom: 'month' };

/** A month's range; the current month runs to today, like the Streamlit version. */
const monthRange = (month: string, today: string): { from: string; to: string } => {
  const [y, m] = month.split('-').map(Number);
  const last = toIso(new Date(Date.UTC(y, m, 0)));
  return { from: `${month}-01`, to: last > today ? today : last };
};

const syncedLabel = (iso: string): string =>
  `${new Date(iso).toLocaleString('en-IN', {
    timeZone: 'Asia/Kolkata', day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit',
  })} IST`;

/**
 * Per-day series for a set of daily rows. Conversion % on a day is that
 * day's conversions over that day's assignments, the Streamlit definition;
 * cumulative mode uses the running totals of both.
 */
function daySeries(days: string[], rows: SalesDailyRow[], cumulative: boolean) {
  const byDay = new Map<string, { assigned: number; converted: number; revenue: number }>();
  rows.forEach((r) => {
    const cur = byDay.get(r.date) ?? { assigned: 0, converted: 0, revenue: 0 };
    cur.assigned += r.assigned;
    cur.converted += r.converted;
    cur.revenue += r.revenue;
    byDay.set(r.date, cur);
  });
  const assigned: number[] = [];
  const converted: number[] = [];
  const revenue: number[] = [];
  let a = 0;
  let c = 0;
  let rev = 0;
  days.forEach((d) => {
    const v = byDay.get(d) ?? { assigned: 0, converted: 0, revenue: 0 };
    a = cumulative ? a + v.assigned : v.assigned;
    c = cumulative ? c + v.converted : v.converted;
    rev = cumulative ? rev + v.revenue : v.revenue;
    assigned.push(a);
    converted.push(c);
    revenue.push(Math.round(rev));
  });
  const conversion = assigned.map((x, i) => ratio(converted[i], x));
  return { assigned, converted, revenue, conversion };
}

const chartOptions = (
  categories: string[],
  colors: string[],
  extra: Partial<ApexOptions> = {},
): ApexOptions => ({
  chart: { toolbar: { show: false }, zoom: { enabled: false }, animations: { enabled: false }, fontFamily: 'inherit' },
  colors,
  stroke: { width: 2, curve: 'straight' },
  markers: { size: 3 },
  dataLabels: { enabled: false },
  legend: { position: 'bottom' },
  grid: { borderColor: '#ededed' }, // --ds-border
  xaxis: { categories, labels: { rotate: -45, hideOverlappingLabels: true, style: { fontSize: '10px' } } },
  ...extra,
});

/** One headline figure. */
/**
 * A tile's comparison with the matching earlier period: counts and money as
 * a % change, a rate (conversion) as percentage points.
 */
type Compare = { now: number; before: number; kind: 'count' | 'money' | 'pp'; vs: string };

const CompareLine: React.FC<{ c: Compare }> = ({ c }) => {
  const diff = c.now - c.before;
  const dir = Math.abs(diff) < 1e-9 ? 0 : diff > 0 ? 1 : -1;
  const tone = dir > 0 ? 'up' : dir < 0 ? 'down' : 'flat';
  const arrow = dir > 0 ? '▲' : dir < 0 ? '▼' : '–';
  const was = c.kind === 'money' ? inr(c.before) : c.kind === 'pp' ? pct1(c.before) : num(c.before);
  let change: string;
  if (c.kind === 'pp') change = `${Math.abs(diff).toFixed(1)} pts`;
  else if (c.before > 0) change = `${Math.abs((diff / c.before) * 100).toFixed(1)}%`;
  else change = dir > 0 ? 'new' : 'no change';
  return (
    <div className={`sales-delta sales-delta--${tone}`} title={`${c.vs}: ${was}`}>
      {arrow} {change} <span className="ds-text-muted">vs {c.vs} · {was}</span>
    </div>
  );
};

const Stat: React.FC<{ label: string; value: string; sub?: string; title?: string; compare?: Compare | null }> = ({
  label, value, sub, title, compare,
}) => (
  <StatTile label={label} value={value} sub={sub} title={title}>
    {compare && <CompareLine c={compare} />}
  </StatTile>
);

/** Assigned, converted and revenue summed over owner rows. */
const totalsOf = (owners: SalesOwnerRow[]) => ({
  assigned: owners.reduce((n, o) => n + o.assigned, 0),
  converted: owners.reduce((n, o) => n + o.converted, 0),
  revenue: owners.reduce((n, o) => n + o.revenue, 0),
});

/** The four headline tiles, each compared with the earlier period when it's loaded. */
const HeadlineTiles: React.FC<{
  owners: SalesOwnerRow[];
  prevOwners: SalesOwnerRow[] | null;
  vs: string;
  conversionSub?: string;
}> = ({ owners, prevOwners, vs, conversionSub = 'converted ÷ assigned' }) => {
  const t = totalsOf(owners);
  const p = prevOwners ? totalsOf(prevOwners) : null;
  const cmp = (now: number, before: number | undefined, kind: Compare['kind']): Compare | null =>
    p && before !== undefined ? { now, before, kind, vs } : null;
  const conv = ratio(t.converted, t.assigned);
  return (
    <div className="sls-stat-row">
      <Stat label="Leads assigned" value={num(t.assigned)} sub="completed demos" compare={cmp(t.assigned, p?.assigned, 'count')} />
      <Stat label="Leads converted" value={num(t.converted)} sub="by conversion date"
        title="Includes leads whose demo was before this range" compare={cmp(t.converted, p?.converted, 'count')} />
      <Stat label="Conversion" value={pct1(conv)} sub={conversionSub}
        compare={cmp(conv, p ? ratio(p.converted, p.assigned) : undefined, 'pp')} />
      <Stat label="Revenue" value={inr(t.revenue)} sub={`ATV ${inr(t.converted ? t.revenue / t.converted : 0)}`}
        compare={cmp(t.revenue, p?.revenue, 'money')} />
    </div>
  );
};
/* Table layout (wrapping sortable headers, sticky owner column) lives in sales.scss. */

/** Owner breakdown for one country, with a totals row. Click any header to sort by it. */
type SortKey =
  | 'leadOwner' | 'assigned' | 'converted' | 'conversionPct' | 'previousConversionPct' | 'trend'
  | 'revenue' | 'atv' | 'totalTalkMinutes' | 'avgTalkMinutes' | 'avgTalkPerDayMinutes' | Cohort;

/** The value each column sorts on. Trend sorts by the change in conversion %. */
const sortValue = (o: SalesOwnerRow, key: SortKey): number | string => {
  switch (key) {
    case 'leadOwner': return o.leadOwner.toLowerCase();
    case 'trend': return o.conversionPct - o.previousConversionPct;
    case 'assigned': case 'converted': case 'conversionPct': case 'previousConversionPct':
    case 'revenue': case 'atv': case 'totalTalkMinutes': case 'avgTalkMinutes': case 'avgTalkPerDayMinutes':
      return o[key];
    default: return o.cohorts[key];
  }
};

export const CountryTable: React.FC<{
  owners: SalesOwnerRow[];
  threshold: number;
  prevLabel: string;
}> = ({ owners, threshold, prevLabel }) => {
  // Biggest first by default (the server's order); numbers sort high→low first, names A→Z.
  const [sort, setSort] = useState<{ key: SortKey; desc: boolean }>({ key: 'assigned', desc: true });
  const sorted = useMemo(() => {
    const dir = sort.desc ? -1 : 1;
    return [...owners].sort((a, b) => {
      const x = sortValue(a, sort.key);
      const y = sortValue(b, sort.key);
      const c = typeof x === 'string' ? x.localeCompare(y as string) : (x as number) - (y as number);
      return c * dir || a.leadOwner.localeCompare(b.leadOwner);
    });
  }, [owners, sort]);

  if (owners.length === 0) return <EmptyState title="No leads in this range." />;

  const sum = (pick: (o: SalesOwnerRow) => number) => owners.reduce((s, o) => s + pick(o), 0);
  const assigned = sum((o) => o.assigned);
  const converted = sum((o) => o.converted);
  const revenue = sum((o) => o.revenue);
  const talking = owners.filter((o) => o.avgTalkMinutes > 0);
  const avgTalk = talking.length ? talking.reduce((s, o) => s + o.avgTalkMinutes, 0) / talking.length : 0;
  const talkDays = sum((o) => o.talkDays);

  /** A clickable header: first click sorts high→low (A→Z for names), the next flips it. */
  const Head: React.FC<{ k: SortKey; label: React.ReactNode; title?: string; align?: 'left' | 'right' | 'center'; sticky?: boolean }> = ({
    k, label, title, align = 'right', sticky,
  }) => {
    const active = sort.key === k;
    return (
      <th
        title={title ? `${title} · click to sort` : 'Click to sort'}
        aria-sort={active ? (sort.desc ? 'descending' : 'ascending') : 'none'}
        onClick={() => setSort(active ? { key: k, desc: !sort.desc } : { key: k, desc: k !== 'leadOwner' })}
        className={[
          'sales-sort',
          `sales-sort--${align}`,
          active && 'sales-sort--active',
          sticky && 'sales-sticky',
        ].filter(Boolean).join(' ')}
      >
        {/* Icon always on the left of the heading; the active column is purple with its direction. */}
        <span className="sales-sort__inner">
          <span className="sales-sort__icon" aria-hidden>
            {active ? (sort.desc ? '▼' : '▲') : '⇅'}
          </span>
          <span>{label}</span>
        </span>
      </th>
    );
  };

  const trendClass = (trend: SalesOwnerRow['trend']) =>
    trend === 'up' ? 'sales-trend--up' : trend === 'down' ? 'sales-trend--down' : 'sales-trend--flat';

  return (
    // Only as wide as its content, with wrapping headers, so columns sit close together.
    <Table caption="Lead owners" className="sales-owner-table">
      <thead>
        <tr>
          <Head k="leadOwner" label="Lead owner" align="left" sticky />
          <Head k="assigned" label="Assigned" />
          <Head k="converted" label="Converted" />
          <Head k="conversionPct" label="Conv %" title={`Red below ${threshold}%`} />
          <Head k="previousConversionPct" label={`${prevLabel} conv %`} />
          <Head k="trend" label="Trend" align="center" title="Change in conversion % vs the earlier period" />
          <Head k="revenue" label="Revenue" title="Payment nearest each conversion, in INR" />
          <Head k="atv" label="ATV" title="Revenue ÷ converted" />
          <Head k="totalTalkMinutes" label="Talk (min)" title="Telephony talk time in the range, all of the agent's calls" />
          <Head k="avgTalkMinutes" label="Avg talk (min)" title="Talk time ÷ completed calls" />
          <Head k="avgTalkPerDayMinutes" label="Avg talk/day (min)" title="Talk time ÷ days with calls (days off don't count)" />
          {COHORTS.map((c) => <Head key={c} k={c} label={c} title="Conversions this many days after the demo" />)}
        </tr>
      </thead>
      <tbody>
        {sorted.map((o) => (
          <tr key={`${o.ownerId}-${o.country}`}>
            <td className="ds-strong sales-sticky">{o.leadOwner}</td>
            <td className="ds-num">{num(o.assigned)}</td>
            <td className="ds-num">{num(o.converted)}</td>
            <td className={o.conversionPct < threshold ? 'ds-num sales-below-bar' : 'ds-num'}>
              {pct1(o.conversionPct)}
            </td>
            <td className="ds-num ds-text-muted">{pct1(o.previousConversionPct)}</td>
            <td className={`sales-trend ${trendClass(o.trend)}`}>
              {o.trend === 'up' ? '▲' : o.trend === 'down' ? '▼' : '—'}
            </td>
            <td className="ds-num">{inr(o.revenue)}</td>
            <td className="ds-num">{inr(o.atv)}</td>
            <td className="ds-num">{o.totalTalkMinutes.toFixed(1)}</td>
            <td className="ds-num">{o.avgTalkMinutes.toFixed(1)}</td>
            <td className="ds-num" title={`${o.talkDays} day${o.talkDays === 1 ? '' : 's'} with calls`}>{o.avgTalkPerDayMinutes.toFixed(1)}</td>
            {COHORTS.map((c) => (
              <td key={c} className={o.cohorts[c] ? 'ds-num' : 'ds-num ds-text-subtle'}>{o.cohorts[c]}</td>
            ))}
          </tr>
        ))}
      </tbody>
      <tfoot>
        <tr>
          <td className="sales-sticky">Total</td>
          <td className="ds-num">{num(assigned)}</td>
          <td className="ds-num">{num(converted)}</td>
          <td className="ds-num">{pct1(ratio(converted, assigned))}</td>
          <td />
          <td />
          <td className="ds-num">{inr(revenue)}</td>
          <td className="ds-num">{inr(converted ? revenue / converted : 0)}</td>
          <td className="ds-num">{sum((o) => o.totalTalkMinutes).toFixed(1)}</td>
          <td className="ds-num" title="Mean of agents with talk time">{avgTalk.toFixed(1)}</td>
          <td className="ds-num" title="All talk time ÷ all days with calls">
            {(talkDays ? sum((o) => o.totalTalkMinutes) / talkDays : 0).toFixed(1)}
          </td>
          {COHORTS.map((c) => <td key={c} className="ds-num">{sum((o) => o.cohorts[c])}</td>)}
        </tr>
      </tfoot>
    </Table>
  );
};

/**
 * Lead owner breakdown as India / USA / ROW tabs: each tab has the region's
 * own headline numbers (assigned, converted, conversion, revenue and ATV)
 * above its owner table. Tabs with no leads are hidden for an agent.
 */
const RegionTabs: React.FC<{
  owners: SalesOwnerRow[];
  prevOwners: SalesOwnerRow[] | null;
  vs: string;
  prevLabel: string;
  hideEmpty: boolean;
}> = ({ owners, prevOwners, vs, prevLabel, hideEmpty }) => {
  const regions = COUNTRIES
    .map((c) => {
      const rows = owners.filter((o) => o.country === c.key);
      const assigned = rows.reduce((n, o) => n + o.assigned, 0);
      const converted = rows.reduce((n, o) => n + o.converted, 0);
      const revenue = rows.reduce((n, o) => n + o.revenue, 0);
      return { ...c, rows, assigned, converted, revenue };
    })
    .filter((r) => !hideEmpty || r.rows.length > 0);
  const [active, setActive] = useState<SalesCountry>(regions[0]?.key ?? 'India');
  useEffect(() => {
    if (regions.length && !regions.some((r) => r.key === active)) setActive(regions[0].key);
  }, [regions, active]);

  const r = regions.find((x) => x.key === active) ?? regions[0];
  if (!r) return <EmptyState title="No leads in this range." />;
  const conversion = ratio(r.converted, r.assigned);

  return (
    <Card>
      <div className="sales-section">
        <Tabs
          label="Lead country"
          tabs={regions.map((x) => ({ key: x.key, label: `${x.flag} ${x.label}`, meta: `· ${num(x.assigned)}` }))}
          active={r.key}
          onChange={setActive}
        />

        <HeadlineTiles owners={r.rows} prevOwners={prevOwners ? prevOwners.filter((o) => o.country === r.key) : null} vs={vs}
          conversionSub={conversion < r.threshold ? `below the ${r.threshold}% bar` : `bar is ${r.threshold}%`} />

        <CountryTable owners={r.rows} threshold={r.threshold} prevLabel={prevLabel} />
      </div>
    </Card>
  );
};

/** One owner's day-wise conversion % for one lead country; hidden when they had no leads from it. */
const RegionTrend: React.FC<{
  country: SalesCountry;
  label: string;
  ownerId: string;
  days: string[];
  rows: SalesDailyRow[];
  cumulative: boolean;
}> = ({ country, label, ownerId, days, rows, cumulative }) => {
  const mine = useMemo(() => rows.filter((r) => r.country === country && r.ownerId === ownerId), [rows, country, ownerId]);
  const series = useMemo(() => daySeries(days, mine, cumulative), [days, mine, cumulative]);

  if (mine.length === 0) return null;

  return (
    <div className="col-12 col-xl-4">
      <Card className="sales-fill" title={label}>
        <Chart
          type="line"
          height={260}
          series={[{ name: 'Conversion %', data: series.conversion }]}
          options={chartOptions(days.map(dayLabel), [BLUE], {
            yaxis: { labels: { formatter: (v: number) => `${Math.round(v)}%` } },
            tooltip: {
              y: {
                formatter: (v: number, opts?: { dataPointIndex: number }) => {
                  const i = opts?.dataPointIndex ?? 0;
                  return `${v}% · ${series.converted[i]} of ${series.assigned[i]} assigned`;
                },
              },
            },
          })}
        />
      </Card>
    </div>
  );
};

const SalesAgentPerformancePage: React.FC = () => {
  const today = istToday();
  const months = useMemo(() => recentMonths(today), [today]);

  const [mode, setMode] = useState<RangeMode>('month');
  const [month, setMonth] = useState(months[0]);
  const [customFrom, setCustomFrom] = useState('');
  const [customTo, setCustomTo] = useState('');
  const [cumulative, setCumulative] = useState(false);

  const [summary, setSummary] = useState<SalesSummary | null>(null);
  /** The matching earlier period (yesterday / last week / same days last month), for the tile comparisons. */
  const [prevSummary, setPrevSummary] = useState<SalesSummary | null>(null);
  const [daily, setDaily] = useState<SalesDailyRow[]>([]);
  const [loading, setLoading] = useState(false);
  const [owner, setOwner] = useState('');
  const [monthly, setMonthly] = useState<SalesOwnerMonthly | null>(null);
  const [monthlyLoading, setMonthlyLoading] = useState(false);


  const range = useMemo(() => {
    if (mode === 'today') return { from: today, to: today };
    if (mode === 'week') return weekRange(today);
    if (mode === 'month') return monthRange(month, today);
    if (customFrom && customTo && customFrom <= customTo) return { from: customFrom, to: customTo };
    return null;
  }, [mode, month, today, customFrom, customTo]);

  const load = useCallback(async (refresh = false) => {
    if (!range) return;
    setLoading(true);
    try {
      const [s, d] = await Promise.all([
        fetchSalesSummary(range.from, range.to, refresh, COMPARE_FOR[mode]),
        fetchSalesDaily(range.from, range.to, refresh),
      ]);
      setSummary(s);
      setDaily(d.rows);
      // The server already worked out the matching earlier period; load its
      // totals too. A failure here only drops the comparison lines.
      setPrevSummary(null);
      fetchSalesSummary(s.previousPeriod.from, s.previousPeriod.to, refresh)
        .then(setPrevSummary)
        .catch(() => setPrevSummary(null));
    } catch (err: any) {
      toast.error(err.message || 'Could not load sales agent performance');
      setSummary(null);
      setPrevSummary(null);
      setDaily([]);
    } finally {
      setLoading(false);
    }
  }, [range, mode]);

  useEffect(() => { void load(); }, [load]);

  /** Owners across countries, for the individual view. Biggest first. */
  const ownerOptions = useMemo(() => {
    const byId = new Map<string, { id: string; name: string; email: string | null; assigned: number; converted: number; revenue: number }>();
    (summary?.owners ?? []).forEach((o) => {
      const cur = byId.get(o.ownerId) ?? { id: o.ownerId, name: o.leadOwner, email: o.ownerEmail, assigned: 0, converted: 0, revenue: 0 };
      cur.assigned += o.assigned;
      cur.converted += o.converted;
      cur.revenue += o.revenue;
      byId.set(o.ownerId, cur);
    });
    return Array.from(byId.values()).sort((a, b) => b.assigned - a.assigned || a.name.localeCompare(b.name));
  }, [summary]);

  useEffect(() => {
    if (!ownerOptions.some((o) => o.id === owner)) setOwner(ownerOptions[0]?.id ?? '');
  }, [ownerOptions, owner]);

  useEffect(() => {
    if (!owner || !range) { setMonthly(null); return; }
    let live = true;
    setMonthlyLoading(true);
    fetchSalesOwnerMonthly(owner, range.to.slice(0, 7), 6, false)
      .then((m) => { if (live) setMonthly(m); })
      .catch((err: any) => { if (live) { setMonthly(null); toast.error(err.message || 'Could not load monthly trend'); } })
      .finally(() => { if (live) setMonthlyLoading(false); });
    return () => { live = false; };
  }, [owner, range]);

  const days = useMemo(() => (range ? eachDay(range.from, range.to) : []), [range]);
  const selected = ownerOptions.find((o) => o.id === owner);
  const ownerSeries = useMemo(
    () => daySeries(days, daily.filter((r) => r.ownerId === owner), cumulative),
    [days, daily, owner, cumulative],
  );

  if (!REPORTS_ENABLED) {
    return (
      <Page>
        <PageHeader eyebrow="Sales" title="Performance" />
        <EmptyState title="Reports aren't enabled" description="Reports aren't enabled in this environment yet." />
      </Page>
    );
  }

  const t = summary?.totals;
  const empty = summary && t!.assigned === 0 && t!.converted === 0;
  const prev = summary?.previousPeriod;
  const prevLabel = prev ? `${dayLabel(prev.from)} – ${dayLabel(prev.to)}` : 'Last month';
  /** What the tiles compare against, in words. */
  const vsLabel =
    mode === 'today' ? 'yesterday' : mode === 'week' ? 'last week' : mode === 'month' ? 'same days last month' : prevLabel;
  const categories = days.map(dayLabel);
  // What the server actually scoped to — not what the switch asked for.
  const isAgent = summary?.viewer.role === 'SALES_AGENT';

  return (
    <Page>
      <PageHeader
        eyebrow="Sales"
        title={isAgent ? 'My Performance' : 'Performance'}
        subtitle={
          isAgent
            ? <>Your leads assigned and converted, how fast they convert, and what they brought in{summary?.viewer.email ? <> · <span className="ds-strong">{summary.viewer.email}</span></> : null}.</>
            : 'Leads assigned and converted by each lead owner, how fast they convert, and what it brought in.'
        }
        actions={
          <Button variant="secondary" icon={<RotateCcw size={16} aria-hidden />} onClick={() => void load(true)}
            title="Refresh from Snowflake" disabled={!range}>
            Refresh
          </Button>
        }
      />

      <Card>
        <Toolbar>
          <div className="ds-field sales-toolbar__fit">
            <span className="ds-field__label">Range</span>
            <SegmentedControl
              label="Date range"
              options={RANGE_MODES.map((m) => ({ value: m.key, label: m.label, title: m.title }))}
              value={mode}
              onChange={setMode}
            />
          </div>
          {mode === 'today' || mode === 'week' ? null : mode === 'month' ? (
            <Field label="Month">
              {(ctl) => (
                <Select {...ctl} options={months.map((m) => ({ value: m, label: monthLabel(m) }))} value={month}
                  onChange={(e) => setMonth(e.target.value)} />
              )}
            </Field>
          ) : (
            <>
              <Field label="From">
                {(ctl) => (
                  <TextInput {...ctl} type="date" value={customFrom} max={today}
                    onChange={(e) => setCustomFrom(e.target.value)} />
                )}
              </Field>
              <Field label="To">
                {(ctl) => (
                  <TextInput {...ctl} type="date" value={customTo} min={customFrom} max={today}
                    onChange={(e) => setCustomTo(e.target.value)} />
                )}
              </Field>
            </>
          )}
          <div className="ds-field sales-toolbar__fit">
            <span className="ds-field__label">Charts</span>
            <SegmentedControl
              label="Chart values"
              options={[{ value: 'daily', label: 'Daily' }, { value: 'cumulative', label: 'Cumulative' }]}
              value={cumulative ? 'cumulative' : 'daily'}
              onChange={(v) => setCumulative(v === 'cumulative')}
            />
          </div>
        </Toolbar>
      </Card>

      {!range ? (
        <EmptyState title="Pick a date range" description="Pick a start and end date to see the dashboard." />
      ) : loading ? (
        <Loader label="Loading sales performance…" />
      ) : !summary ? (
        <EmptyState title="Nothing to show." description="Try another range, or Refresh." />
      ) : (
        <>
          <p className="sales-note ds-text-muted">
            Showing <span className="ds-strong">{longDay(range.from)} – {longDay(range.to)}</span>
            {summary.lastSyncedAt && <> · Salesforce last synced <span className="ds-strong">{syncedLabel(summary.lastSyncedAt)}</span></>}
          </p>

          {/* The week (Sunday–Saturday) the picked range ends in; arrows on the card move week by week. */}
          {isAgent && <ThisWeeksIncentive initialDay={range.to} />}

          {empty ? (
            <Card>
              <EmptyState
                title="No leads in this range"
                description={isAgent
                  ? <>No leads whose Salesforce owner email is <span className="ds-strong">{summary.viewer.email}</span> had a completed demo or converted between these dates. If that&apos;s wrong, the CMS and Salesforce emails may differ.</>
                  : 'No demos were completed and no leads converted between these dates.'}
              />
            </Card>
          ) : (
            <>
              <HeadlineTiles owners={summary.owners} prevOwners={prevSummary?.owners ?? null} vs={vsLabel} />

              <div className="row g-3">
                {isAgent && (
                  <div className="col-12 col-lg-6 col-xl-5">
                    {/* The month of the selected range; the server ranks this agent within their region. */}
                    <WhereIStand month={range.to.slice(0, 7)} />
                  </div>
                )}
                <div className={isAgent ? 'col-12 col-lg-6 col-xl-7' : 'col-12 col-xl-4'}>
                  <Card className="sales-fill" title="How fast leads convert"
                    subtitle="Days from the demo to conversion, as a share of all conversions.">
                    <div className="ds-stack">
                      {t!.cohorts.map((c) => (
                        <div key={c.cohort} className="sales-meter">
                          <div className="sales-meter__row">
                            <span className="ds-strong">{c.cohort}</span>
                            <span className="ds-num">
                              {num(c.count)} <span className="ds-text-muted">· {pct1(c.pct)}</span>
                            </span>
                          </div>
                          <ProgressBar value={c.pct} label={`${c.cohort}: ${pct1(c.pct)} of conversions`} />
                        </div>
                      ))}
                    </div>
                  </Card>
                </div>
                <div className={isAgent ? 'col-12' : 'col-12 col-xl-8'}>
                  <Card className="sales-fill" title={isAgent ? 'Day by day' : 'Team, day by day'}
                    subtitle={`Assigned by demo day, converted by conversion day${cumulative ? ', running totals' : ''}.`}>
                    <Chart
                      type="line"
                      height={280}
                      series={(() => {
                        const s = daySeries(days, daily, cumulative);
                        return [{ name: 'Assigned', data: s.assigned }, { name: 'Converted', data: s.converted }];
                      })()}
                      options={chartOptions(categories, [BLUE, GREEN])}
                    />
                  </Card>
                </div>
              </div>

              <div className="sales-heading">
                <h2 className="sales-heading__title">{isAgent ? 'My leads by country' : 'Lead owner breakdown'}</h2>
                <p className="sales-heading__text ds-text-muted">
                  Split by the lead&apos;s country, one tab each. Conversion % shows red below the country&apos;s bar; the trend compares it with {prevLabel}.
                  Click any column heading to sort by it; click again to reverse.
                </p>
              </div>
              <RegionTabs owners={summary.owners} prevOwners={prevSummary?.owners ?? null} vs={vsLabel}
                prevLabel={prevLabel} hideEmpty={isAgent} />

              <div className="sales-heading sales-heading--split">
                <h2 className="sales-heading__title">{isAgent ? 'My trend' : 'Individual lead owner'}</h2>
                {!isAgent && (
                  <Field label="Lead owner" className="sales-owner-picker">
                    {(ctl) => (
                      <Select {...ctl} size="sm" options={ownerOptions.map((o) => ({ value: o.id, label: o.name }))}
                        value={owner} onChange={(e) => setOwner(e.target.value)} />
                    )}
                  </Field>
                )}
              </div>
              {selected && (
                <>
                  {/* An agent's headline numbers and day chart above are already theirs alone. */}
                  {!isAgent && (
                    <>
                      {/* Same tiles as the top of the page, for this owner, compared with the earlier period. */}
                      <HeadlineTiles owners={summary.owners.filter((o) => o.ownerId === owner)}
                        prevOwners={prevSummary ? prevSummary.owners.filter((o) => o.ownerId === owner) : null} vs={vsLabel} />
                      {selected.email && (
                        <ThisWeeksIncentive email={selected.email} name={selected.name.split(' ')[0]} initialDay={range.to} />
                      )}
                      <div className="row g-3">
                        <div className="col-12 col-lg-6 col-xl-5">
                          {selected.email ? (
                            <WhereIStand month={range.to.slice(0, 7)} email={selected.email}
                              title={`Where ${selected.name.split(' ')[0]} stands`} />
                          ) : (
                            <Card className="sales-fill">
                              <EmptyState title="No ranking" description="Unassigned leads have no owner to rank." />
                            </Card>
                          )}
                        </div>
                        <div className="col-12 col-lg-6 col-xl-7">
                          <Card className="sales-fill" title="Assigned and converted">
                            <Chart type="line" height={260}
                              series={[{ name: 'Assigned', data: ownerSeries.assigned }, { name: 'Converted', data: ownerSeries.converted }]}
                              options={chartOptions(categories, [BLUE, GREEN])} />
                          </Card>
                        </div>
                      </div>
                    </>
                  )}

                  {daily.some((r) => r.ownerId === owner) && (
                    <>
                      <h3 className="sales-subheading">Day-wise conversion rate by region{isAgent ? '' : ` — ${selected.name}`}</h3>
                      <div className="row g-3">
                        {COUNTRIES.map((c) => (
                          <RegionTrend key={c.key} country={c.key} label={`${c.flag} ${c.label}`} ownerId={owner}
                            days={days} rows={daily} cumulative={cumulative} />
                        ))}
                      </div>
                    </>
                  )}
                  <div className="row g-3">
                    <div className="col-12 col-lg-6">
                      <Card className="sales-fill" title="Conversion % (day-wise)">
                        <Chart type="line" height={240}
                          series={[{ name: 'Conversion %', data: ownerSeries.conversion }]}
                          options={chartOptions(categories, [AMBER], { yaxis: { labels: { formatter: (v: number) => `${Math.round(v)}%` } } })} />
                      </Card>
                    </div>
                    <div className="col-12 col-lg-6">
                      <Card className="sales-fill" title="Revenue (day-wise)">
                        <Chart type="bar" height={240}
                          series={[{ name: 'Revenue', data: ownerSeries.revenue }]}
                          options={chartOptions(categories, [GREEN], {
                            stroke: { width: 0 },
                            yaxis: { labels: { formatter: (v: number) => inr(v) } },
                          })} />
                      </Card>
                    </div>
                  </div>

                  <Card title={`Month on month — ${isAgent ? 'you' : selected.name} (last 6 months)`}>
                    {monthlyLoading ? (
                      <Loader label="Loading monthly trend…" />
                    ) : !monthly ? (
                      <EmptyState title="Nothing to show." />
                    ) : (
                      <div className="row g-3">
                        {/* Full width, scrolling if it must: beside a chart, the table overflowed under it. */}
                        <div className="col-12">
                          <Table caption="Month on month">
                            <thead>
                              <tr>
                                <th>Month</th>
                                <th className="ds-num">Assigned</th>
                                <th className="ds-num">Converted</th>
                                <th className="ds-num">Conv %</th>
                                <th className="ds-num">Revenue</th>
                              </tr>
                            </thead>
                            <tbody>
                              {monthly.months.map((m) => (
                                <tr key={m.month}>
                                  <td className="sales-nowrap">{monthLabel(m.month)}</td>
                                  <td className="ds-num">{num(m.assigned)}</td>
                                  <td className="ds-num">{num(m.converted)}</td>
                                  <td className="ds-num">{pct1(m.conversionPct)}</td>
                                  <td className="ds-num">{inr(m.revenue)}</td>
                                </tr>
                              ))}
                            </tbody>
                          </Table>
                        </div>
                        <div className="col-12 col-lg-4">
                          <div className="sales-chart-title ds-text-muted">Assigned and converted</div>
                          <Chart type="line" height={220}
                            series={[
                              { name: 'Assigned', data: monthly.months.map((m) => m.assigned) },
                              { name: 'Converted', data: monthly.months.map((m) => m.converted) },
                            ]}
                            options={chartOptions(monthly.months.map((m) => monthLabel(m.month)), [BLUE, GREEN])} />
                        </div>
                        <div className="col-12 col-lg-4">
                          <div className="sales-chart-title ds-text-muted">Conversion % trend</div>
                          <Chart type="line" height={220}
                            series={[{ name: 'Conversion %', data: monthly.months.map((m) => m.conversionPct) }]}
                            options={chartOptions(monthly.months.map((m) => monthLabel(m.month)), [ORANGE], {
                              yaxis: { labels: { formatter: (v: number) => `${Math.round(v)}%` } },
                            })} />
                        </div>
                        <div className="col-12 col-lg-4">
                          <div className="sales-chart-title ds-text-muted">Revenue trend</div>
                          <Chart type="line" height={220}
                            series={[{ name: 'Revenue', data: monthly.months.map((m) => m.revenue) }]}
                            options={chartOptions(monthly.months.map((m) => monthLabel(m.month)), [PURPLE], {
                              yaxis: { labels: { formatter: (v: number) => inr(v) } },
                            })} />
                        </div>
                      </div>
                    )}
                  </Card>
                </>
              )}
            </>
          )}

          <p className="sales-footnote ds-text-muted">
            Dates are IST days. Assigned = leads whose demo was completed that day; converted = leads converted that day,
            whenever their demo was. Revenue is the student&apos;s payment nearest the conversion, in INR. Talk time is
            telephony talk time for the agent across all their calls, so it repeats on each country they appear in.
          </p>
        </>
      )}
    </Page>
  );
};

export default SalesAgentPerformancePage;
